import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  MagnifyingGlass,
  MapPin,
  Phone,
  CurrencyDollar,
  Storefront,
  User,
  CalendarBlank,
  Camera,
} from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";

export default function AdminCustomerSearchPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("visits");

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleSearch = async () => {
    if (!search.trim()) {
      toast.error("Please enter a customer name to search");
      return;
    }

    const token = localStorage.getItem("adminToken");
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("search", search);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await axios.get(`${API}/admin/customer-activity?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
    } catch (error) {
      toast.error("Failed to search customer activity");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy 'at' h:mm a") + " IST";
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-customer-search">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 -ml-2 text-[#9CA3AF] hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <p className="text-[#9CA3AF] text-sm">Admin</p>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Customer Activity Search
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Search Form */}
        <div className="card-flat p-4 space-y-4">
          <div>
            <Label htmlFor="search" className="text-[#0A0A0A] font-medium">
              Customer Name / Company
            </Label>
            <div className="relative mt-1.5">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company name or contact name..."
                className="pl-10"
                data-testid="customer-search-input"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startDate" className="text-[#0A0A0A] font-medium">
                From Date
              </Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1.5"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label htmlFor="endDate" className="text-[#0A0A0A] font-medium">
                To Date
              </Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1.5"
                data-testid="end-date-input"
              />
            </div>
          </div>

          <Button
            onClick={handleSearch}
            disabled={loading}
            className="w-full bg-[#0055FF] hover:bg-[#0040CC]"
            data-testid="search-button"
          >
            {loading ? "Searching..." : "Search Activity"}
          </Button>
        </div>

        {/* Results */}
        {results && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-2">
              <div className="stat-card text-center p-3">
                <MapPin size={20} className="mx-auto text-[#10B981] mb-1" weight="fill" />
                <p className="font-bold text-[#0A0A0A]">{results.total_visits}</p>
                <p className="text-xs text-[#9CA3AF]">Visits</p>
              </div>
              <div className="stat-card text-center p-3">
                <Phone size={20} className="mx-auto text-[#8B5CF6] mb-1" weight="fill" />
                <p className="font-bold text-[#0A0A0A]">{results.total_calls}</p>
                <p className="text-xs text-[#9CA3AF]">Calls</p>
              </div>
              <div className="stat-card text-center p-3">
                <CurrencyDollar size={20} className="mx-auto text-[#F59E0B] mb-1" weight="fill" />
                <p className="font-bold text-[#0A0A0A]">{results.total_sales}</p>
                <p className="text-xs text-[#9CA3AF]">Sales</p>
              </div>
              <div className="stat-card text-center p-3">
                <Storefront size={20} className="mx-auto text-[#0055FF] mb-1" weight="fill" />
                <p className="font-bold text-[#0A0A0A]">${results.total_sales_amount?.toLocaleString() || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Total</p>
              </div>
            </div>

            {/* Matching Customers */}
            {results.customers?.length > 0 && (
              <div className="card-flat p-4">
                <h3 className="font-bold text-[#0A0A0A] mb-3">Matching Customers ({results.customers.length})</h3>
                <div className="space-y-2">
                  {results.customers.slice(0, 5).map((customer) => (
                    <div key={customer.id} className="flex items-center gap-3 p-2 bg-[#F3F4F6] rounded-lg">
                      <Storefront size={20} className="text-[#4B5563]" weight="fill" />
                      <div>
                        <p className="font-medium text-[#0A0A0A]">{customer.company_name || customer.name}</p>
                        {customer.company_name && customer.name !== customer.company_name && (
                          <p className="text-sm text-[#4B5563]">{customer.name}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {results.customers.length > 5 && (
                    <p className="text-sm text-[#9CA3AF] text-center">
                      +{results.customers.length - 5} more customers
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2">
              {["visits", "calls", "sales"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2 px-3 rounded-full font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? "bg-[#0A0A0A] text-white"
                      : "bg-white border border-[#E5E7EB] text-[#4B5563]"
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} ({results[tab]?.length || 0})
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="card-flat overflow-hidden">
              {activeTab === "visits" && (
                results.visits?.length > 0 ? (
                  results.visits.map((visit, index) => (
                    <div key={visit.id} className="p-4 border-b border-[#E5E7EB] last:border-b-0">
                      <div className="flex items-start gap-3">
                        {visit.photo_path ? (
                          <div className="w-16 h-16 rounded-lg bg-[#F3F4F6] flex items-center justify-center overflow-hidden">
                            <Camera size={24} className="text-[#9CA3AF]" />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                            <MapPin size={24} className="text-[#9CA3AF]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-[#0A0A0A]">{visit.company_name}</p>
                          <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                            <User size={14} />
                            <span>{visit.salesperson_name}</span>
                          </div>
                          <p className="text-sm text-[#9CA3AF]">{formatDate(visit.created_at)}</p>
                          {visit.latitude && visit.longitude && (
                            <p className="text-xs text-[#10B981] mt-1">
                              GPS: {visit.latitude.toFixed(4)}, {visit.longitude.toFixed(4)}
                              {visit.distance_from_prev && ` (${visit.distance_from_prev} km from prev)`}
                            </p>
                          )}
                          {visit.notes && (
                            <p className="text-sm text-[#9CA3AF] mt-1">{visit.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-[#9CA3AF]">No visits found</div>
                )
              )}

              {activeTab === "calls" && (
                results.calls?.length > 0 ? (
                  results.calls.map((call, index) => (
                    <div key={call.id} className="p-4 border-b border-[#E5E7EB] last:border-b-0">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          call.call_type === "inbound" ? "bg-[#D1FAE5]" : "bg-[#DBEAFE]"
                        }`}>
                          <Phone size={20} className={call.call_type === "inbound" ? "text-[#10B981]" : "text-[#0055FF]"} weight="fill" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#0A0A0A]">{call.company_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              call.call_type === "inbound" ? "bg-[#D1FAE5] text-[#059669]" : "bg-[#DBEAFE] text-[#2563EB]"
                            }`}>
                              {call.call_type}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                            <User size={14} />
                            <span>{call.salesperson_name}</span>
                          </div>
                          <p className="text-sm text-[#9CA3AF]">{formatDate(call.created_at)}</p>
                          {call.notes && (
                            <p className="text-sm text-[#9CA3AF] mt-1">{call.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-[#9CA3AF]">No calls found</div>
                )
              )}

              {activeTab === "sales" && (
                results.sales?.length > 0 ? (
                  results.sales.map((sale, index) => (
                    <div key={sale.id} className="p-4 border-b border-[#E5E7EB] last:border-b-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#0A0A0A]">{sale.company_name}</p>
                          <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                            <User size={14} />
                            <span>{sale.salesperson_name}</span>
                          </div>
                          <p className="text-sm text-[#9CA3AF]">{formatDate(sale.created_at)}</p>
                          {sale.description && (
                            <p className="text-sm text-[#9CA3AF]">{sale.description}</p>
                          )}
                        </div>
                        <p className="font-bold text-[#10B981] text-lg">${sale.amount?.toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-[#9CA3AF]">No sales found</div>
                )
              )}
            </div>
          </>
        )}

        {results === null && (
          <div className="card-flat p-8 text-center">
            <MagnifyingGlass size={48} className="mx-auto text-[#D1D5DB] mb-3" />
            <p className="text-[#9CA3AF]">Search for a customer to view their activity</p>
            <p className="text-sm text-[#9CA3AF] mt-1">
              See all visits, calls, and sales across all salespeople
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
